import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import {
  QuoteStep,
  QuoteData,
  QuoteStatus,
  QuoteErrorCode,
  QuoteErrorType,
  QuoteStatusSummary,
  QuoteCustomerFacingStatus,
  QuoteErrorData,
} from '@typesApp/QuoteData'
import { QuoteDetails } from '@typesApp/QuoteDetails'
import { config } from '@config/config'
import { formatPrice } from './formatPrice'

const isInsurerOffline = (quote: QuoteData | undefined): boolean => {
  return (
    quote?.status === QuoteStatus.CONNECTION_ERROR &&
    quote?.errorType === QuoteErrorType.COMMUNICATION_ERROR &&
    quote?.errorCode === QuoteErrorCode.INSURER_OFFLINE
  )
}

export const getQuoteStep = (options: { claimRecord: ClaimRecord; quoteRequested?: boolean }): QuoteStep => {
  const claimRecordStatus = options.claimRecord?.status
  const quote = options.claimRecord?.claimData?.quote
  const quoteStatus = quote?.status || QuoteStatus.NONE

  // quote accepted
  if (QuoteStatus.CX_ACCEPTED === quoteStatus || ClaimRecordStatus.CLAIM_ACCEPTED === claimRecordStatus) {
    return QuoteStep.ACCEPTED
  }

  // not yet submitted or submit pending quote
  if (
    [QuoteStatus.NONE, QuoteStatus.SUBMITTED, QuoteStatus.RETRY_CANCELLED, QuoteStatus.REJECTED].includes(quoteStatus)
  ) {
    return QuoteStep.REQUEST
  }

  // request quote error or accept error
  if (
    [QuoteStatus.ERROR, QuoteStatus.CX_ACCEPT_FAILED, QuoteStatus.CLOSED].includes(quoteStatus) &&
    claimRecordStatus === ClaimRecordStatus.QUOTE_REQUIRED
  ) {
    return QuoteStep.REQUEST
  }

  // quote requested and insurer offline
  if (claimRecordStatus === ClaimRecordStatus.QUOTE_REQUESTED) {
    return isInsurerOffline(quote) ? QuoteStep.OFFLINE : QuoteStep.REQUEST
  }

  // ready to submit claim
  if (
    [QuoteStatus.APPROVED, QuoteStatus.CX_ACCEPTED].includes(quoteStatus) ||
    (quoteStatus === QuoteStatus.CX_REFUSED && claimRecordStatus === ClaimRecordStatus.READY_TO_CLAIM)
  ) {
    return QuoteStep.SUBMIT
  }

  // insurer offline - allow request quote again if previous attempt was offline
  if (isInsurerOffline(quote)) {
    return options.quoteRequested ? QuoteStep.OFFLINE : QuoteStep.REQUEST
  }

  return QuoteStep.NONE
}

export const getLastQuoteStatusSummary = (claimRecord?: ClaimRecord): QuoteStatusSummary => {
  const lastResult = claimRecord?.claimData?.quoteSummaries?.find(s => s.lastQuote)
  return lastResult
    ? {
        ...lastResult,
        claimRecordStatus: claimRecord?.status,
      }
    : {
        status: claimRecord?.claimData?.quote?.status ?? QuoteStatus.NONE,
        customerFacingStatus: claimRecord?.claimData?.quote?.customerFacingStatus ?? QuoteCustomerFacingStatus.NONE,
        errorType: claimRecord?.claimData?.quote?.errorType,
        errorCode: claimRecord?.claimData?.quote?.errorCode,
        errorMessage: claimRecord?.claimData?.quote?.errorMessage,
        claimRecordStatus: claimRecord?.status,
      }
}

const getQuoteErrorData = (claimRecord: ClaimRecord): QuoteErrorData | undefined => {
  const quote = claimRecord?.claimData?.quote
  return (
    quote && {
      errorType: quote?.errorType,
      errorCode: quote?.errorCode,
      errorMessage: quote?.errorMessage,
    }
  )
}

export const getQuoteDetails = (claimRecord: ClaimRecord): QuoteDetails => {
  const quote = claimRecord?.claimData?.quote
  const subtotal = claimRecord?.orderSubtotal?.amount ?? 0
  const confirmed = claimRecord.claimData?.quote?.quoteConfirmedAmount?.amount ?? 0

  // free shipping by default unless disabled for brand
  const disableFreeShipping = config.getFeatures()?.disableQuoteFreeShipping
  const shipping = !disableFreeShipping ? 0 : (quote?.shippingCosts?.amount ?? 0)
  const isFreeShipping = shipping === 0

  return {
    status: quote?.status || QuoteStatus.NONE,
    insurerName: quote?.primaryCoverage?.insurer?.name || '',
    orderSubtotal: formatPrice(subtotal),
    shippingCosts: formatPrice(shipping),
    residualAmount: formatPrice(subtotal + shipping - confirmed),
    requestedAmount: formatPrice(confirmed),
    confirmedAmount: formatPrice(confirmed),
    isFreeShipping,
    error: getQuoteErrorData(claimRecord),
  }
}

export const isQuoteRefusedStatus = (status: QuoteStatus | undefined): boolean => status === QuoteStatus.CX_REFUSED
